import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import devRulesService from "../api/devRulesService";

const DevRulesLockContext = createContext({
  lockState: null,
  isLoading: false,
  refreshLockState: async () => {},
  acknowledgeLatestAndRefresh: async () => {},
});

export const DevRulesLockProvider = ({ children, enabled = true }) => {
  const [lockState, setLockState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const refreshLockState = useCallback(async () => {
    if (!enabled) {
      setLockState(null);
      return null;
    }

    setIsLoading(true);
    try {
      const response = await devRulesService.getLockState();
      const nextState = response?.data || null;
      setLockState(nextState);
      return nextState;
    } catch (error) {
      setLockState(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  const acknowledgeLatestAndRefresh = useCallback(async () => {
    if (!enabled) return null;
    await devRulesService.acknowledgeLatest();
    return refreshLockState();
  }, [enabled, refreshLockState]);

  useEffect(() => {
    if (!enabled) {
      setLockState(null);
      return;
    }

    refreshLockState();

    // Keep lock state fresh across long sessions.
    const interval = setInterval(() => {
      refreshLockState();
    }, 120000);

    return () => clearInterval(interval);
  }, [enabled, refreshLockState]);

  return (
    <DevRulesLockContext.Provider
      value={{
        lockState,
        isLoading,
        refreshLockState,
        acknowledgeLatestAndRefresh,
      }}
    >
      {children}
    </DevRulesLockContext.Provider>
  );
};

export const useDevRulesLock = () => useContext(DevRulesLockContext);

export default DevRulesLockContext;
